import { MouseEvent, useState } from 'react';

import { Button, CircularProgress, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { ApiKeyCreate } from '../../../../types/apiTypes';
import ApiKeysContainer from '../../ApiKeysContainer';
import DescriptionInput from './FormComponents/DescriptionInput';
import ExpirationSelect from './FormComponents/ExpirationSelect';
import KeyNameInput from './FormComponents/KeyNameInput';
import Scopes from './FormComponents/Scopes';
import SuccessDialog from './FormComponents/SuccessDialog';
import KeysBreadCrumbs from './KeysBreadCrumbs';
import { AdminControlCheckbox } from '../../utils/AdminControlCheckbox';

const ButtonsDiv = styled.div`
    margin-top: 24px;
    display: flex;
    justify-content: flex-start;
`;

const KeyCreate: React.FC = () => {
    const { t } = useTranslation('admin_settings');
    const { path } = useRouteMatch();
    const history = useHistory();
    const { createApiKey, isSaving, selectedKey, keyValue } = useContainer(ApiKeysContainer);
    const [openSuccessDialog, setOpenSuccessDialog] = useState<boolean>(false);
    const [formValues, setFormValues] = useState<ApiKeyCreate>({
        name: '',
        description: '',
        is_active: false,
        expires_at: '',
        scopes: []
    });

    const handleChange = (event) => {
        const {
            target: { name, value }
        } = event;
        setFormValues({
            ...formValues,
            [name]: value
        });
    };

    const handleScopesChange = (event) => {
        const {
            target: { name, checked }
        } = event;
        let newScopes = formValues.scopes;
        if (checked) {
            newScopes.push(name);
            if (name.includes('write')) {
                const fieldRead = name.replace('write', 'read');
                if (!newScopes.includes(fieldRead)) {
                    newScopes.push(fieldRead);
                }
            }
        } else {
            newScopes = newScopes.filter((scope) => scope !== name);
        }
        setFormValues({
            ...formValues,
            scopes: newScopes
        });
    };

    const handleIsActiveChange = (event) => {
        const {
            target: { checked }
        } = event;
        setFormValues({
            ...formValues,
            is_active: checked
        });
    };

    const handleSave = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const expirationDate = formValues.expires_at?.includes('no-expiration') ? null : formValues.expires_at;
        await createApiKey({
            ...formValues,
            expires_at: expirationDate
        });
        setOpenSuccessDialog(true);
    };

    const handleSuccessClose = () => {
        setOpenSuccessDialog(false);
        history.push(path.replace('/create', ''));
    };

    return (
        <div>
            <Typography variant="h1">{t('api-keys.new-api-key')}</Typography>
            <KeysBreadCrumbs keyName={t('create')} />
            <KeyNameInput name={formValues.name} handleChange={handleChange} />
            <DescriptionInput description={formValues.description} handleChange={handleChange} />
            <ExpirationSelect expiresAt={formValues.expires_at} handleChange={handleChange} />
            <Scopes scopes={formValues.scopes} handleChange={handleScopesChange} />
            <AdminControlCheckbox
                sx={{ marginTop: '24px' }}
                label={t('api-keys.enable-key')}
                control={
                    <Switch
                        name="is_active"
                        value={formValues.is_active}
                        checked={formValues.is_active}
                        onChange={handleIsActiveChange}
                    />
                }
            />
            <ButtonsDiv>
                <Button
                    sx={{ padding: 'auto 12px' }}
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={!formValues.name || !formValues.description}
                >
                    {isSaving ? <CircularProgress size={20} color="inherit" /> : t('save')}
                </Button>
            </ButtonsDiv>
            <SuccessDialog
                open={openSuccessDialog}
                handleClose={handleSuccessClose}
                createdKey={selectedKey}
                secret={keyValue}
            />
        </div>
    );
};

export default KeyCreate;
